import React, { useState, useCallback, useMemo, memo, useEffect } from 'react';

const AppImage = memo(function AppImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  fill = false,
  onClick,
  fallbackSrc = '/assets/images/no_image.png',
  loading = 'lazy',
  style,
  ...props
}) {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setImageSrc(src);
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  const handleError = useCallback(() => {
    if (!hasError && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(true);
    }
    setIsLoading(false);
  }, [hasError, imageSrc, fallbackSrc]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const imageClassName = useMemo(() => {
    const classes = [className];
    if (isLoading) classes.push('bg-gray-200');
    if (onClick) classes.push('cursor-pointer hover:opacity-90 transition-opacity duration-200');
    if (fill) classes.push('w-full h-full');
    return classes.filter(Boolean).join(' ');
  }, [className, isLoading, onClick, fill]);

  const img = (
    <img
      src={imageSrc}
      alt={alt}
      width={fill ? undefined : width || 400}
      height={fill ? undefined : height || 300}
      className={imageClassName}
      loading={priority ? 'eager' : loading}
      onError={handleError}
      onLoad={handleLoad}
      onClick={onClick}
      style={fill ? { width: '100%', height: '100%', ...(style || {}) } : style}
      {...props}
    />
  );

  if (fill) {
    return (
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        {img}
      </div>
    );
  }

  return img;
});

AppImage.displayName = 'AppImage';

export default AppImage;
